/**
 * The client card's `⋯` — ending the relationship, the entry the roster's preview panel gave up,
 * under whatever the host puts above it (the card's „Przypisz ankietę" and „Ustawienia rozliczeń").
 *
 * KANON v3 draws this control beside the `‹ Poprzednia / Następna ›` walk. `ClientDetailScreen`
 * carried a note saying it was deliberately not built, because "every entry it would carry is on
 * the roster row's own menu, and inventing a second home for the same three acts is not a thing
 * this screen needs". On 16 September 2026 the owner emptied that menu, so the reason expired and
 * the control is built where the canon put it.
 *
 * It draws NOTHING when there is no client id — a screen that cannot name the subject cannot offer
 * to end a relationship with them, and a dead affordance is worse than an absent one.
 */

import { Menu } from '@base-ui/react/menu';
import { useTranslation } from 'react-i18next';

import { clientConfirmPrompt } from '~/entities/client';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import menuStyles from '~/shared/ui/MenuSurface.module.css';

import { useEndCoaching } from '../lib/useEndCoaching';

import { MoreGlyph } from '~/shared/ui/MoreGlyph';

import styles from './EndCoachingMenu.module.css';

interface EndCoachingMenuProps {
  readonly customerId: number | null;
  /** The client's name, for the confirmation's subject line. Never interpolated into the question. */
  readonly clientName: string;
  /** Run once the server accepts: this client is no longer on the trainer's roster. */
  readonly onEnded: () => void;
  /**
   * The entries above „Zakończ współpracę", in order, ruled off from it — the client card's
   * „Przypisz ankietę" and „Ustawienia rozliczeń" (T-K1). The host owns what each opens; this menu
   * only offers them.
   */
  readonly leadingItems?: readonly EndCoachingMenuItem[];
}

export interface EndCoachingMenuItem {
  /** Stable per entry; React's key. */
  readonly id: string;
  readonly label: string;
  readonly onSelect: () => void;
}

export function EndCoachingMenu({
  customerId,
  clientName,
  onEnded,
  leadingItems = [],
}: EndCoachingMenuProps) {
  const { t } = useTranslation();
  const act = useEndCoaching(customerId, onEnded);

  if (customerId === null) return null;

  return (
    <>
      <Menu.Root>
        {/* An icon-only trigger carries its whole name in `aria-label`, and `title` as well, because
          * there is no visible text to read under a pointer. */}
        <Menu.Trigger
          className={styles.trigger}
          title={t('web:clients.preview.more')}
          aria-label={t('web:clients.preview.moreFor', { name: clientName })}
        >
          <MoreGlyph className={styles.glyph} />
        </Menu.Trigger>
        <Menu.Portal>
          <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="end">
            <Menu.Popup className={menuStyles.popup}>
              {leadingItems.map((item) => (
                <Menu.Item key={item.id} className={menuStyles.item} onClick={item.onSelect}>
                  <span className={menuStyles.itemLabel}>{item.label}</span>
                </Menu.Item>
              ))}
              {leadingItems.length === 0 ? null : <Menu.Separator className={menuStyles.separator} />}
              <Menu.Item
                className={`${menuStyles.item} ${menuStyles.itemDestructive}`}
                onClick={() => {
                  act.ask();
                }}
              >
                <span className={menuStyles.itemLabel}>{t('web:clients.row.endCoaching')}</span>
              </Menu.Item>
            </Menu.Popup>
          </Menu.Positioner>
        </Menu.Portal>
      </Menu.Root>

      {/* A refusal REPLACES the dialog's description and the dialog stays open, so the instruction
        * lands where the eyes already are and the act it refers to is still on screen. See
        * `useEndCoaching` for why this screen must not grow a second live region. */}
      <ConfirmDialog
        prompt={
          act.confirming
            ? {
                ...clientConfirmPrompt('endCoaching', clientName, t, act.busy),
                ...(act.refusal === null ? {} : { description: act.refusal }),
              }
            : null
        }
        onConfirm={act.confirm}
        onCancel={act.dismiss}
      />
    </>
  );
}
